// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { Pill } from "components/elements";
import { GrabsonIcon } from "components/layout";
import { isAllowed } from "containers/Permissions";
import { useUser } from "hooks/users";
import { INVALID_ID } from "pages/Applications/AppDetails/constants";
import { NO_VALUE_PLACEHOLDER } from "src/constants";
import { trans } from "src/translations";
import { classes } from "utils/classes";

import styles from "./User.scss";

interface UserProps {
  className?: string;
  userId?: string;
  onClick?: () => void;
  requiredRoles?: string[];
}

export function User({ className, userId, onClick, requiredRoles }: UserProps) {
  const { data, isError, isLoading } = useUser(userId);

  const getUserName = () => {
    const user = data?.results;
    return isError
      ? trans.APP_DETAILS_MODERATION__UNKNOWN_USER({ id: userId || "-" })
      : [user?.given_name, user?.family_name].join(" ");
  };

  const renderUser = () => {
    const user = data?.results;
    return (
      <div>
        <GrabsonIcon
          name={isError ? "warning" : "users"}
          size="normal"
          className={isError ? styles.iconWarn : styles.icon}
        />
        <span
          onClick={onClick}
          className={classes(styles.button, {
            [styles.disabled]: Boolean(!onClick),
          })}
        >
          {isLoading ? (
            "..."
          ) : (
            <>
              {getUserName()}
              {!user?.is_active && (
                <Pill text={trans.USER_LIST__DEACTIVATED()} icon="users" />
              )}
              {!isAllowed(user, requiredRoles) && (
                <Pill text={trans.USER_LIST__NO_ACCESS()} icon="users" />
              )}
            </>
          )}
        </span>
      </div>
    );
  };

  const renderButton = () => (
    <div>
      {NO_VALUE_PLACEHOLDER}
      {onClick && (
        <span onClick={onClick} className={styles.button}>
          {`(${trans.CHANGE()})`}
        </span>
      )}
    </div>
  );

  return (
    <div className={className}>
      {userId && userId !== INVALID_ID ? renderUser() : renderButton()}
    </div>
  );
}
